import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Archive, Zap, Calendar, ChartNoAxesGantt, Package, PlayCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const DemoVideoThumbnail = () => {
  return (
    <div className="relative aspect-[3/2] w-full overflow-hidden rounded-lg border border-muted bg-muted/30">
      <iframe
        title="vimeo-player"
        src="https://player.vimeo.com/video/1108764129?h=4255df269e"
        width="640"
        height="360"
        frameBorder="0"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
      />
    </div>
  );
};

export const ReleaseFeatureAnnouncement = () => {
  const [open, setOpen] = useState(false);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const dismissed = localStorage.getItem('release-feature-dismissed') === 'true';
    if (!dismissed) setOpen(true);

    const handleOpenFromEvent = (e) => {
      const targetSlide = typeof e.detail?.slide === 'number' ? e.detail.slide : 2;
      setSlide(Math.max(0, Math.min(2, targetSlide)));
      setOpen(true);
    };
    window.addEventListener('open-release-feature-video', handleOpenFromEvent);
    return () => {
      window.removeEventListener('open-release-feature-video', handleOpenFromEvent);
    };
  }, []);

  const dismiss = () => {
    setOpen(false);
    localStorage.setItem('release-feature-dismissed', 'true');
  };

  const next = () => setSlide((s) => Math.min(s + 1, 2));
  const prev = () => setSlide((s) => Math.max(s - 1, 0));

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-background/20 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-background border border-muted rounded-lg shadow-xl max-w-2xl w-full p-0 overflow-hidden">
        {/* Top bar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-muted">
          <div className="text-xs font-medium text-muted-foreground">New: Release Groups</div>
          <div className="flex items-center gap-1">
            {[0, 1, 2].map((i) => (
              <span key={i} className={`h-1.5 w-6 rounded-full ${slide === i ? 'bg-primary' : 'bg-muted'}`} />
            ))}
          </div>
        </div>

        <div className="p-6">
          {slide === 0 && (
            <div className="space-y-8">
              <div className="text-center space-y-2">
                <h2 className="text-3xl font-semibold tracking-tight bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Introducing Release Groups
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Plan work in Backlog, move releases to your Active week, and ship with confidence.
                </p>
              </div>

              <div className="space-y-5">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-neutral-75 dark:bg-neutral-800/50 rounded-lg flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Plan releases in Backlog</p>
                    <p className="text-sm text-muted-foreground">Plan work before it's ready</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-neutral-75 dark:bg-neutral-800/50 rounded-lg flex items-center justify-center">
                    <Package className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Group Tasks by Release</p>
                    <p className="text-sm text-muted-foreground">Organize tasks into releases and track progress together</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-neutral-75 dark:bg-neutral-800/50 rounded-lg flex items-center justify-center">
                    <ChartNoAxesGantt className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">Release View (Beta)</p>
                    <p className="text-sm text-muted-foreground">Visualize releases in a Gantt-style timeline</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end pt-2">
                <Button size="sm" onClick={next}>
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {slide === 1 && (
            <div className="space-y-8">
              <div className="text-center space-y-2">
                <h3 className="text-3xl font-semibold tracking-tight bg-gradient-to-r from-green-500 via-emerald-400 to-lime-300 bg-clip-text text-transparent">
                  Backlog is no longer a status
                </h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  It's your planning space. Use releases to group work, or move tasks directly.
                </p>
              </div>

              {/* Two-column comparison */}
              <div className="grid grid-cols-2 gap-6 max-w-lg mx-auto">
                {/* Backlog */}
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-neutral-75 dark:bg-neutral-800/50 rounded-lg flex items-center justify-center">
                      <Archive className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <p className="font-medium">Backlog</p>
                  </div>
                </div>
                {/* Active week */}
                <div className="flex flex-col items-center text-center space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-12 h-12 bg-neutral-75 dark:bg-neutral-800/50 rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <p className="font-medium">Active week</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end pt-2">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={prev}>
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Back
                  </Button>
                  <Button size="sm" onClick={next}>
                    Next
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {slide === 2 && (
            <div className="space-y-8">
              <div className="text-center space-y-2">
                <h3 className="text-3xl font-semibold tracking-tight bg-gradient-to-r from-amber-400 via-orange-500 to-rose-400 bg-clip-text text-transparent">Watch a quick demo</h3>
                <p className="text-base text-muted-foreground leading-relaxed">See how to configure releases and plan your week.</p>
              </div>

              <DemoVideoThumbnail />

              <div className="flex items-center justify-end pt-2">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={prev}>
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Back
                  </Button>
                  <Button size="sm" onClick={dismiss}>Finish</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReleaseFeatureAnnouncement;
